import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { HomeBodyBannerSection } from "../home.styles";
import { IBannerReponse } from "../../../model/base/response/banner-response";
import { useMenuLinkNavigation } from "../../hooks/useMenuLinkNavigation";

/**
 * Banner de corpo da home customizada (seção Banners): card informativo na largura do corpo, com a imagem ao lado
 * de um painel na cor do portal. Layout propositalmente diferente do carrossel do topo (BannerSlide).
 */
export default function BodyBanner({ banners }: { banners: IBannerReponse[] }) {
  const [active, setActive] = useState(0);
  const openLink = useMenuLinkNavigation();
  const total = banners.length;

  useEffect(() => {
    if (total < 2) return;
    const timer = setInterval(() => setActive((current) => (current + 1) % total), 6000);
    return () => clearInterval(timer);
  }, [total]);

  if (total === 0) return null;

  const banner = banners[active % total];
  const navSx = { color: "inherit", border: "1px solid rgba(255,255,255,.5)", "&:hover": { backgroundColor: "rgba(255,255,255,.15)" } };

  return (
    <HomeBodyBannerSection>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" },
          height: { md: 240 },
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid rgba(0,0,0,.08)",
          backgroundColor: "#fff",
        }}
      >
        <Box
          component="img"
          src={banner.urlImage}
          alt={banner.title || "Banner"}
          sx={{ width: "100%", height: { xs: 160, sm: 200, md: "100%" }, objectFit: "cover", display: "block" }}
        />

        <Box
          sx={{
            backgroundColor: "var(--portal-primary)",
            color: "#fff",
            p: { xs: 2.5, md: 3 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 2,
            minWidth: 0,
          }}
        >
          {banner.title && (
            <Typography
              component="h3"
              sx={{
                fontSize: { xs: "1.1rem", md: "1.35rem" },
                fontWeight: 700,
                lineHeight: 1.3,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {banner.title}
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {banner.url && (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => openLink(banner.url, banner.openInNewTab ?? true)}
                sx={{
                  backgroundColor: "#fff",
                  color: "var(--portal-secondary)",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "var(--portal-accent)" },
                }}
              >
                Saiba mais
              </Button>
            )}
            {total > 1 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                <IconButton size="small" aria-label="Banner anterior" sx={navSx} onClick={() => setActive((active - 1 + total) % total)}>
                  <KeyboardArrowLeft fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 36, textAlign: "center" }}>
                  {(active % total) + 1} / {total}
                </Typography>
                <IconButton size="small" aria-label="Próximo banner" sx={navSx} onClick={() => setActive((active + 1) % total)}>
                  <KeyboardArrowRight fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </HomeBodyBannerSection>
  );
}
